import { Injectable, inject, PLATFORM_ID, effect } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { io, Socket } from 'socket.io-client';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

@Injectable({
    providedIn: 'root'
})
export class SocketService {
    private socket!: Socket;
    private authService = inject(AuthService);
    private notificationSubject = new Subject<any>();
    public notifications$ = this.notificationSubject.asObservable();

    constructor() {
        this.socket = io(environment.SOCKET_URL, {
            withCredentials: true,
            transports: ['websocket'],
            autoConnect: false // Connect manually
        });

        // Setup the specialized user notification listener
        this.setupUserListener();

        // Reactive connection management based on auth state
        effect(() => {
            const user = this.authService.currentUser();
            if (user?.id) {
                this.connect();
            } else {
                this.disconnect();
            }
        });
    }

    /**
     * Setup consolidated user notification listener
     */
    private setupUserListener() {
        const event = 'user:notification';
        this.socket.on(event, (data: any) => {
            console.log(`Received WebSocket event [${event}]:`, data);
            this.notificationSubject.next({ event, data });
        });

        this.socket.on('connect', () => {
            console.log('Successfully connected to WebSocket server');
            const user = this.authService.currentUser();
            if (user?.id) {
                console.log('Joining user room:', user.id);
                this.socket.emit('join-user-room', user.id);
            } else {
                console.warn('Socket connected but no user ID found for room joining');
            }
        });
        //connection error handling
        this.socket.on('disconnect', (reason) => {
            console.log('Socket disconnected:', reason);
        });
        // Handle connection errors
        this.socket.on('connect_error', (error) => {
            console.error('Socket connection error:', error);
        });

        this.socket.on('error', (error) => {
            console.error('Socket error:', error);
        });
    }

    /**
     * Manually connects to the socket server.
     */
    connect() {
        if (this.socket) {
            // Token query param removed in favor of cookies
            // this.socket.io.opts.query = ...

            if (!this.socket.connected) {
                this.socket.connect();
            }
        }
    }

    /**
     * Disconnects from the socket server.
     * Will only disconnect if the socket is already connected.
     */
    disconnect() {
        if (this.socket && this.socket.connected) {
            this.socket.disconnect();
        }
    }

    /**
     * Listen for events from the socket server.
     * Returns an Observable that emits the event data when the event is triggered.
     * The Observable will automatically unsubscribe when it is completed.
     *
     * @param {string} eventName - The name of the event to listen for.
     * @returns {Observable<T>} - An Observable that emits the event data.
     */
    onEvent<T>(eventName: string): Observable<T> {
        return new Observable((subscriber) => {
            if (!this.socket) {
                return;
            }

            this.socket.on(eventName, (data: T) => {
                subscriber.next(data);
            });

            return () => {
                this.socket.off(eventName);
            };
        });
    }

    /**
     * Emit an event to the server
     */
    emit(event: string, data: any) {
        if (this.socket) {
            this.socket.emit(event, data);
        }
    }
}
